import { useState } from 'react'
import type { UserFontSizePreference } from '@alpha-edge/shared'
import { PageHeader, Alert, card, btnPrimary, colors, label as labelStyle, input } from '../components/ui'
import { FONT_SIZE_LABELS, FONT_SIZE_PX, usePreferences } from '../hooks/use-preferences'

const FONT_SIZES: readonly UserFontSizePreference[] = ['sm', 'md', 'lg', 'xl']

export function PreferencesPage() {
  const { preferences, isLoading, setFontSize, error } = usePreferences()
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<string | null>(null)

  const current = preferences.fontSize ?? 'md'

  const handleSelect = async (size: UserFontSizePreference) => {
    setSaving(true)
    setSaved(null)
    try {
      await setFontSize(size)
      setSaved(`Font size updated to ${FONT_SIZE_LABELS[size]} (${FONT_SIZE_PX[size]}px base).`)
    } catch {
      // error surfaced via the context
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PageHeader title="Preferences" subtitle="Personal UI settings saved to your account" />
      {error && <Alert type="error" message={error} />}
      {saved && <Alert type="success" message={saved} />}

      <div style={card}>
        <h2 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Font Size</h2>
        <p style={{ margin: '0 0 1rem', color: colors.textMuted, fontSize: '0.875rem' }}>
          Adjusts the base font size for every page. Everything else in the UI (including the left nav sidebar) is
          rem-based and scales with this setting -- sidebar text stays a step smaller than the main content.
        </p>

        <div style={{ display: 'grid', gap: '0.5rem', maxWidth: '400px' }}>
          <label style={labelStyle}>
            Select size
            <select
              style={{ ...input, marginTop: '0.25rem' }}
              value={current}
              onChange={(e) => handleSelect(e.target.value as UserFontSizePreference)}
              disabled={isLoading || saving}
              data-testid="font-size-select"
            >
              {FONT_SIZES.map((size) => (
                <option key={size} value={size}>
                  {FONT_SIZE_LABELS[size]} -- {FONT_SIZE_PX[size]}px
                </option>
              ))}
            </select>
          </label>

          <div
            aria-live="polite"
            style={{
              padding: '0.75rem',
              border: `1px solid ${colors.border}`,
              borderRadius: '3px',
              background: '#fafafa',
              marginTop: '0.25rem',
            }}
          >
            <div style={{ fontSize: '0.75rem', color: colors.textMuted, marginBottom: '0.25rem' }}>Preview</div>
            <div style={{ fontSize: '1rem' }}>
              The quick brown fox jumps over the lazy dog. Patient MRN <strong>000123</strong> -- glucose{' '}
              <strong>142 mg/dL</strong>.
            </div>
          </div>

          <button
            type="button"
            style={{ ...btnPrimary, marginTop: '0.5rem', width: 'fit-content' }}
            onClick={() => handleSelect('md')}
            disabled={isLoading || saving || current === 'md'}
          >
            Reset to default
          </button>
        </div>
      </div>
    </div>
  )
}
